import { useEffect, useMemo, useState } from "react";
import { addDays, differenceInCalendarDays, startOfDay } from "date-fns";
import { api, toISO } from "@/lib/api";

export const useAvailability = () => {
  const [unavailable, setUnavailable] = useState(new Set());
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    api.get("/availability")
      .then((r) => setUnavailable(new Set(r.data.unavailable)))
      .finally(() => setLoading(false));
  }, []);
  return { unavailable, loading };
};

// Check-in days must fit the minimum stay; while picking check-out, only valid check-outs stay enabled.
export const useStayPicker = (minNights, initial) => {
  const { unavailable, loading } = useAvailability();
  const [range, setRange] = useState(initial);
  const disabled = useMemo(() => {
    const today = startOfDay(new Date());
    const from = range?.from && !range?.to ? range.from : null;
    const nextTaken = from ? [...unavailable].sort().find((d) => d >= toISO(from)) || null : null;
    const fitsStay = (day) => {
      for (let i = 0; i < minNights; i++) if (unavailable.has(toISO(addDays(day, i)))) return false;
      return true;
    };
    return (day) => {
      if (day < today) return true;
      if (from && day > from) {
        if (differenceInCalendarDays(day, from) < minNights) return true;
        return !!nextTaken && toISO(day) > nextTaken;
      }
      return !fitsStay(day);
    };
  }, [unavailable, range, minNights]);
  const onSelect = (_next, day) => {
    if (!day) return;
    if (range?.from && !range.to && day > range.from) setRange({ from: range.from, to: day });
    else setRange({ from: day, to: undefined });
  };
  const picking = !!(range?.from && !range?.to);
  const hint = picking ? `Now choose a check-out date at least ${minNights} nights after check-in.` : `Minimum stay: ${minNights} nights.`;
  return { range, setRange, onSelect, disabled, loading, hint, picking };
};
